import React, { useEffect, useMemo, useState } from "react";
import { Badge, Button, Card, CardHeader, Col, Container, FloatingLabel, Form, InputGroup, Modal, Nav, Row, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import { useNavigate, useSearchParams } from "react-router-dom";
import Navbar from "./Navbar";
import PageTitle from "./components/PageTitle";
import RichTextEditor from "./components/RichTextEditor";
import { API_ENDPOINTS } from "./config/api";
import UserAvatar from "./components/UserAvatar";
import { Pin, Plus, PinOff, ThumbsUp, ThumbsDown, MessageCircleMore, Eye, MessageCircleDashed} from "lucide-react";

const DiscussionsPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const userRole = localStorage.getItem("role") || "";

  const [loading, setLoading] = useState(true);
  const [catsLoading, setCatsLoading] = useState(true);
  const [trendingLoading, setTrendingLoading] = useState(true);

  const [categories, setCategories] = useState([]);
  const [posts, setPosts] = useState([]);
  const [trending, setTrending] = useState([]);

  const [q, setQ] = useState(searchParams.get("q") || "");
  const [activeCategoryId, setActiveCategoryId] = useState(searchParams.get("categoryId") || "all");
  const [activeHashtag, setActiveHashtag] = useState(searchParams.get("hashtag") || "");
  const [sort, setSort] = useState(searchParams.get("sort") || "newest");

  const [showCreatePost, setShowCreatePost] = useState(false);
  const [showCreateCategory, setShowCreateCategory] = useState(false);

  const [creatingPost, setCreatingPost] = useState(false);
  const [creatingCategory, setCreatingCategory] = useState(false);

  const [postForm, setPostForm] = useState({
    title: "",
    categoryId: "",
    contentHtml: "",
    hashtags: "",
    audience: "all",
    allowedRoles: [],
    allowedUserIds: []
  });

  const [categoryForm, setCategoryForm] = useState({
    name: "",
    description: "",
    themeColor: "#3b82f6"
  });

  const token = useMemo(() => localStorage.getItem("token"), []);

  const fetchCategories = async () => {
    setCatsLoading(true);
    try {
      const res = await fetch(`${API_ENDPOINTS.DISCUSSIONS}/categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error("Failed");
      const data = await res.json();
      setCategories(Array.isArray(data) ? data : []);
    } catch (e) {
      setCategories([]);
    } finally {
      setCatsLoading(false);
    }
  };

  const fetchTrending = async () => {
    setTrendingLoading(true);
    try {
      const res = await fetch(`${API_ENDPOINTS.DISCUSSIONS}/hashtags/trending`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error("Failed");
      const data = await res.json();
      setTrending(Array.isArray(data) ? data : []);
    } catch (e) {
      setTrending([]);
    } finally {
      setTrendingLoading(false);
    }
  };

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set("q", q.trim());
      if (activeCategoryId && activeCategoryId !== "all") params.set("categoryId", activeCategoryId);
      if (activeHashtag) params.set("hashtag", activeHashtag);
      if (sort) params.set("sort", sort);

      const res = await fetch(`${API_ENDPOINTS.DISCUSSIONS}/posts?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error("Failed");
      const data = await res.json();
      setPosts(Array.isArray(data) ? data : []);
    } catch (e) {
      setPosts([]);
      toast.error("Failed to load discussions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
    fetchTrending();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (q.trim()) next.set("q", q.trim());
    else next.delete("q");
    if (activeCategoryId && activeCategoryId !== "all") next.set("categoryId", activeCategoryId);
    else next.delete("categoryId");
    if (activeHashtag) next.set("hashtag", activeHashtag);
    else next.delete("hashtag");
    if (sort) next.set("sort", sort);
    else next.delete("sort");
    setSearchParams(next, { replace: true });

    fetchPosts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, activeCategoryId, activeHashtag, sort]);

  const togglePostPin = async (postId) => {
    const prev = posts;
    setPosts((list) => list.map((p) => (p.id === postId ? { ...p, pinned: !p.pinned } : p)));
    try {
      const res = await fetch(`${API_ENDPOINTS.DISCUSSIONS}/posts/${postId}/pin`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || "Failed");
      await fetchPosts();
      toast.success(data.pinned ? "Pinned" : "Unpinned");
    } catch (e) {
      setPosts(prev);
      toast.error("Failed to toggle pin");
    }
  };

  const toggleCategoryPin = async (categoryId) => {
    const prev = categories;
    setCategories((list) => list.map((c) => (String(c.id) === String(categoryId) ? { ...c, pinned: !c.pinned } : c)));
    try {
      const res = await fetch(`${API_ENDPOINTS.DISCUSSIONS}/categories/${categoryId}/pin`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || "Failed");
      await fetchCategories();
      toast.success(data.pinned ? "Pinned category" : "Unpinned category");
    } catch (e) {
      setCategories(prev);
      toast.error("Failed to toggle category pin");
    }
  };

  const resetPostForm = () => {
    setPostForm({
      title: "",
      categoryId: "",
      contentHtml: "",
      hashtags: "",
      audience: "all",
      allowedRoles: [],
      allowedUserIds: []
    });
  };

  const resetCategoryForm = () => {
    setCategoryForm({ name: "", description: "", themeColor: "#3b82f6" });
  };

  const createCategory = async () => {
    if (!categoryForm.name.trim()) return toast.error("Category name is required");
    setCreatingCategory(true);
    try {
      const res = await fetch(`${API_ENDPOINTS.DISCUSSIONS}/categories`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          name: categoryForm.name.trim(),
          description: categoryForm.description,
          themeColor: categoryForm.themeColor
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || "Failed");
      toast.success("Category created");
      setShowCreateCategory(false);
      resetCategoryForm();
      await fetchCategories();
    } catch (e) {
      toast.error(e.message || "Failed to create category");
    } finally {
      setCreatingCategory(false);
    }
  };

  const createPost = async () => {
    if (!postForm.title.trim()) return toast.error("Title is required");
    if (!postForm.contentHtml || !postForm.contentHtml.trim()) return toast.error("Content is required");
    setCreatingPost(true);
    try {
      const res = await fetch(`${API_ENDPOINTS.DISCUSSIONS}/posts`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          title: postForm.title.trim(),
          contentHtml: postForm.contentHtml,
          categoryId: postForm.categoryId ? Number(postForm.categoryId) : null,
          hashtags: postForm.hashtags,
          audience: postForm.audience,
          allowedRoles: postForm.allowedRoles,
          allowedUserIds: postForm.allowedUserIds
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || "Failed");
      toast.success("Discussion posted");
      setShowCreatePost(false);
      resetPostForm();
      await fetchTrending();
      await fetchPosts();
    } catch (e) {
      toast.error(e.message || "Failed to create post");
    } finally {
      setCreatingPost(false);
    }
  };

  const activeCategory = useMemo(() => {
    if (!activeCategoryId || activeCategoryId === "all") return null;
    return categories.find((c) => String(c.id) === String(activeCategoryId)) || null;
  }, [activeCategoryId, categories]);

  const getFirstImageSrc = (html) => {
    if (typeof document === "undefined" || !html) return null;
    try {
      const wrapper = document.createElement("div");
      wrapper.innerHTML = html;
      const img = wrapper.querySelector("img");
      return img ? img.getAttribute("src") : null;
    } catch {
      return null;
    }
  };

  const getTextSnippet = (html, maxLength = 180) => {
    if (typeof document === "undefined" || !html) return "";
    try {
      const wrapper = document.createElement("div");
      wrapper.innerHTML = html;
      const text = (wrapper.textContent || wrapper.innerText || "").trim().replace(/\s+/g, " ");
      if (text.length <= maxLength) return text;
      return `${text.slice(0, maxLength)}…`;
    } catch {
      return "";
    }
  };

  const getPlaceholderSwatch = (seed) => {
    const swatches = [
      { bg: "#FF5733", fg: "#FFFFFF" }, // vibrant orange
      { bg: "#00D084", fg: "#FFFFFF" }, // bright green
      { bg: "#2196F3", fg: "#FFFFFF" }, // vivid blue
      { bg: "#FFB300", fg: "#FFFFFF" }, // bold yellow
      { bg: "#D72660", fg: "#FFFFFF" }, // magenta/red
      { bg: "#9400D3", fg: "#FFFFFF" }, // vibrant purple
      { bg: "#FF0080", fg: "#FFFFFF" }  // hot pink
    ];
    const n = Number.isFinite(seed)
      ? seed
      : String(seed || "")
          .split("")
          .reduce((a, c) => a + c.charCodeAt(0), 0);
    const idx = Math.abs(n) % swatches.length;
    return swatches[idx];
  };

  const truncateTitle = (title, maxChars = 60) => {
    const t = (title || "").trim();
    if (t.length <= maxChars) return t;
    return `${t.slice(0, maxChars)}…`;
  };

  return (
    <>
      <Navbar />
      <PageTitle title="Discussions" />
      <div className="bg-white shadow-sm custom-sticky-top">
        <Container>
          <Row className="align-items-center">
            <Col lg={2} md={6} sm={6} className="col-6">
              <h6 className="mb-0 fw-semibold py-3">Discussions</h6>
            </Col>
            <Col lg={10} md={6} sm={6} className="order-last order-lg-2 order-md-3 order-sm-3 py-1">
            <div className="d-flex flex-row justify-content-end align-items-center gap-2">
            <Form.Group>
            <Form.Control
                          type="text"
                          value={q}
                          onChange={(e) => setQ(e.target.value)}
                          placeholder="Search discussions"
                          size="sm"
                        />
                        </Form.Group>                    
                        <Button
                      variant="outline-primary"
                      size="sm"
                      className="d-flex align-items-center gap-2"
                      onClick={() => setShowCreatePost(true)}
                    >
                       <Plus /> <span className="d-none d-md-inline">New post</span>
                    </Button>
            </div>
            </Col>
          </Row>
        </Container>
      </div>
      <Container className="py-4">
        <Row className="g-4">
          {/* Sidebar - left */}
          <Col lg={2} className="mb-4">
            <Card className="border-0 bg-white shadow-sm mb-3 p-0"> 
              <CardHeader className="border-0 px-3 py-3 d-flex justify-content-between align-items-center">
                  <h6 className="mb-0 fw-semibold">Topics</h6>
                  {userRole === "director" && (
                          <Button
                            variant="light-solid"
                            size="sm"
                            className="px-2 py-1 "
                            onClick={() => setShowCreateCategory(true)}
                            title="Add category"
                          >
                            <Plus />
                   </Button>
                  )}
                 
                </CardHeader>
              <Card.Body className="pe-0 ps-2 pt-0 pb-2">
              <div className="d-flex justify-content-center align-items-center">
                {catsLoading && <Spinner animation="border" variant="primary" size="sm" className="align-middle"></Spinner>}
                </div>
                {categories.length === 0 ? (
                  <div className="text-muted small">
                    No categories yet{userRole === "director" ? ". Create one to organize discussions." : "."}
                  </div>
                ) : (
                  <Nav variant="pills" className="flex-column gap-1">
                    <Nav.Item>
                      <Nav.Link
                        active={activeCategoryId === "all"}
                        className="text-start p-2"
                        onClick={() => {
                          setActiveHashtag("");
                          setActiveCategoryId("all");
                        }}
                      >
                        All discussions
                      </Nav.Link>
                    </Nav.Item>
                    {categories.map((c) => (
                      <Nav.Item key={c.id}>
                        <Nav.Link
                          active={String(activeCategoryId) === String(c.id)}
                          className="text-start p-2"
                          onClick={() => {
                            setActiveHashtag("");
                            setActiveCategoryId(String(c.id));
                          }}
                        >
                          <span className="d-flex align-items-center gap-2">
                            <span
                              className="rounded-circle"
                              style={{
                                width: 6,
                                height: 6,
                                background: c.themeColor || "#64748b",
                                display: "inline-block"
                              }}
                            />
                            <span className="text-break">{c.name}</span>
                            {c.pinned && (
                              <span className="ms-auto"><Pin fill="var(--yellow)"  style={{ width: 16, height: 16 }}/></span>
                            )}
                          </span>
                          {c.description ? (
                            <div className="small text-muted text-break">{c.description}</div>
                          ) : null}
                        </Nav.Link>
                      </Nav.Item>
                    ))}
                  </Nav>
                )}
                
              </Card.Body>
            </Card>

            {userRole === "director" && activeCategoryId !== "all" && (
              <Card className="border-0 bg-white shadow-sm mb-3">
                <Card.Body className="">
                  <h6 className="mb-2 fw-semibold">Category options</h6>
                  <Button
                    size="sm"
                    variant={activeCategory?.pinned ? "light-solid" : "outline-secondary"}
                    className="d-flex align-items-center gap-2"
                    onClick={() => toggleCategoryPin(activeCategoryId)}
                    disabled={!activeCategory}
                  >
                    <span className="d-flex align-items-center gap-2">
                      {activeCategory?.pinned ? (
                        <>
                          <PinOff fill="var(--gray-500)" style={{ width: 16, height: 16 }} />
                          <span className="d-none d-md-inline">Unpin category</span>
                        </>
                      ) : (
                        <>
                          <Pin fill="var(--yellow)" style={{ width: 16, height: 16 }} />
                          <span className="d-none d-md-inline">Pin category</span>
                        </>
                      )}
                    </span>
               
                  </Button>
                </Card.Body>
              </Card>
            )}

            <Card className="border-0 bg-white shadow-sm">
              <Card.Body className="p-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h6 className="mb-0 fw-semibold">Trending hashtags</h6>
                </div>
                <div className="d-flex justify-content-center align-items-center">
                {trendingLoading && <Spinner animation="border" variant="primary" size="sm" className="align-middle"></Spinner>}
                </div>
                {trending.length === 0 ? (
                  <div className="text-muted small">No trending hashtags yet.</div>
                ) : (
                  <div className="d-flex flex-wrap gap-2">
                    {trending.map((h) => (
                      <Badge
                        key={h.tag}
                        bg="outline-light"
                        className="small cursor-pointer"
                        style={{ cursor: "pointer" }}
                        onClick={() => {
                          setActiveCategoryId("all");
                          setActiveHashtag(h.tag);
                        }}
                      >
                        #{h.tag} <span className="text-muted">({h.count})</span>
                      </Badge>
                    ))}
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>

          {/* Main content - right */}
          <Col lg={10}>
            <Card className="border-0 bg-white shadow-sm">
              <Card.Header className="d-flex flex-wrap border-0 align-items-center justify-content-between px-4 py-3">
                  <div className="d-flex flex-column">
                    <h6 className="mb-0 fw-semibold">Discussions</h6>
                    <div className="text-muted small">
                      {activeCategory ? (
                        <>
                          Filtering by <span className="fw-semibold">{activeCategory.name}</span>
                        </>
                      ) : activeHashtag ? (
                        <>
                          Filtering by <span className="fw-semibold">#{activeHashtag.replace(/^#/, "")}</span>
                        </>
                      ) : (
                        "Browse and join program discussions."
                      )}
                    </div>
                  </div>
                    <div style={{ minWidth: 100 }}>
                        <Form.Select value={sort} onChange={(e) => setSort(e.target.value)} size="sm">
                          <option value="newest">Newest</option>
                          <option value="trending">Trending</option>
                          <option value="mostViewed">Most viewed</option>
                        </Form.Select>
                      
                    </div>
                </Card.Header>
              <Card.Body className="p-4">
                {/* Submenu bar (search, filters, actions) */}
                

                {/*{(activeCategoryId !== "all" || activeHashtag) && (
                  <div className="d-flex flex-wrap gap-2 mb-3">
                    {activeCategoryId !== "all" && (
                      <Badge bg="outline-light" className="small">
                        Category: {activeCategory?.name || "Selected"}
                      </Badge>
                    )}
                    {activeHashtag && (
                      <Badge bg="outline-light" className="small">
                        Hashtag: #{activeHashtag.replace(/^#/, "")}
                      </Badge>
                    )}
                    <Button
                      variant="link"
                      className="p-0 text-decoration-none fw-semibold small"
                      onClick={() => {
                        setActiveCategoryId("all");
                        setActiveHashtag("");
                      }}
                    >
                      Clear filters
                    </Button>
                  </div>
                )}  */}

                {loading ? (
                  <div className="text-center py-5">
                    <Spinner animation="border" size="sm" className="me-2" />
                    <span className="text-muted">Loading discussions…</span>
                  </div>
                ) : posts.length === 0 ? (
                  <div className="py-5 d-flex flex-column align-items-center justify-content-center">
                    <span className="bg-light rounded-2 px-3 py-2">
                       <MessageCircleDashed  style={{ width: 48, height: 48 }} />
                    </span>
                    <div className="mt-2 fw-semibold">No discussions yet</div>
                    <div className="text-muted small">Be the first to start a topic.</div>
                    {/*<Button className="mt-3" variant="primary" onClick={() => setShowCreatePost(true)}>
                      Create a post
                    </Button>*/}
                  </div>
                ) : (
                  <div className="d-flex flex-column gap-3">
                    {posts.map((p) => (
                      <Card key={p.id} className="border-alt-light custom-hover-bg-alt rounded-4"style={{ cursor: "pointer" }}
                      onClick={() => navigate(`/discussions/${p.id}`)}>
                        <Card.Body className="p-3">
                          <Row className="g-3 align-items-center">
                            <Col md={3} lg={3}>
                              {(() => {
                                const imgSrc = getFirstImageSrc(p.contentHtml);
                                if (imgSrc) {
                                  return (
                                    <div className="ratio ratio-4x3 rounded-2 overflow-hidden bg-light">
                                      <img
                                        src={imgSrc}
                                        alt={p.title}
                                        className="w-100 h-100"
                                        style={{ objectFit: "cover" }}
                                      />
                                    </div>
                                  );
                                }
                                return (
               
                                      <div
                                        className="h-100 position-relative discussion-img-placeholder d-flex flex-column p-3 rounded-2 overflow-hidden h-100"
                                        style={{
                                          minHeight: 150,
                                          backgroundColor: getPlaceholderSwatch(p.id).bg
                                        }}
                                      >
                                        <div className="small" style={{ color: "rgba(255,255,255,0.85)" }}>
                                          {p.category?.name || "General"}
                                        </div>
                                        <div className="flex-grow-1 d-flex align-items-end">
                                          <div
                                            className="fw-bold discussion-img-text"
                                          >
                                            {truncateTitle(p.title, 80)}
                                          </div>
                                        </div>
                                      </div>

                                );
                              })()}
                            </Col>
                            <Col md={9} lg={9}>
                              <div className="d-flex flex-row justify-content-between align-items-center gap-1 mb-1">
                              <div className="d-flex flex-column align-items-start">
                              <h6
                                className="fw-semibold mb-0"
                                style={{ cursor: "pointer" }}
                                onClick={() => navigate(`/discussions/${p.id}`)}
                              >
                                {p.title}
                              </h6>
                              <div className="text-muted small">
                                {new Date(p.createdAt).toLocaleString()}
                              </div>
                              </div>
                              <div className="d-flex flex-row justify-content-end align-items-center gap-1">
                              {userRole === "director" && (
                                  <Button
                                    size="sm"
                                    variant={p.pinned ? "light-solid" : "light-lite"}
                                    onClick={() => togglePostPin(p.id)}
                                    className="px-2 py-1"
                                    title={p.pinned ? "Unpin" : "Pin"}
                                  >
                                    {p.pinned ? <PinOff fill="var(--gray-500)" style={{ width: 16, height: 16 }} /> : <Pin fill="var(--yellow)" style={{ width: 16, height: 16 }} />}
                                  </Button>
                              )}
                             
                                {p.pinned && (
                                  <Badge bg="light" className="border d-flex align-items-center gap-2">
                                    <Pin fill="var(--yellow)" style={{ width: 16, height: 16 }} />
                                    <span className="d-none d-md-inline">Pinned</span>
                                  </Badge>
                                )}
                                {p.category?.name && (
                                  <span
                                    className="badge rounded-pill"
                                    style={{
                                      background: (p.category.themeColor || "#64748b") + "20",
                                      color: p.category.themeColor || "#64748b",
                                    }}
                                  >
                                    {p.category.name}
                                  </span>
                                )}
                              
                              </div>
                              
                              </div>

                              <div className="text-muted small mb-2">
                                {getTextSnippet(p.contentHtml)}
                              </div>
                              {Array.isArray(p.hashtags) && p.hashtags.length > 0 && (
                                <div className="d-flex flex-wrap gap-1 mb-2">
                                  {p.hashtags.slice(0, 5).map((t) => (
                                    <Badge
                                      key={t}
                                      bg="outline-light"
                                      className="small cursor-pointer"
                                      style={{ cursor: "pointer" }}
                                      onClick={() => {
                                        setActiveCategoryId("all");
                                        setActiveHashtag(t);
                                      }}
                                    >
                                      #{t}
                                    </Badge>
                                  ))}
                                </div>
                              )}
                              <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                                <div className="d-flex align-items-center gap-2 small text-muted">
                                  <UserAvatar user={p.creator} size={32} alt="" />
                                  <span className="fw-semibold text-body">
                                    {p.creator?.firstName} {p.creator?.lastName}
                                  </span>
                                </div>
                                <div className="d-flex align-items-center gap-3 small text-muted">
                                  <div className="d-flex flex-row align-items-center gap-3 rounded-pill bg-secondary p-1 px-2">
                                  <span className="d-flex align-items-center gap-1">
                                     <ThumbsUp style={{ width: 16, height: 16 }} /> {p.likeCount || 0}
                                  </span>
                                  <span className="d-flex align-items-center gap-1">
                                    <ThumbsDown style={{ width: 16, height: 16 }} /> {p.dislikeCount || 0}
                                  </span></div>
                                  <span className="d-flex align-items-center gap-1">
                                    <MessageCircleMore style={{ width: 16, height: 16 }} /> {p.commentCount || 0}
                                  </span>
                                  <span className="d-flex align-items-center gap-1">
                                    <Eye style={{ width: 16, height: 16 }} /> {p.viewCount || 0}
                                  </span>
                                </div>
                              </div>
                            </Col>
                          </Row>
                        </Card.Body>
                      </Card>
                    ))}
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Create Category (director) */}
      <Modal
        show={showCreateCategory}
        onHide={() => {
          setShowCreateCategory(false);
          resetCategoryForm();
        }}
      >
        <Modal.Header closeButton className="border-bottom-0 px-4">
          <Modal.Title className="w-100">
            <div className="d-flex flex-column justify-content-start align-items-start">
              <span>New Category</span>
              <div className="text-hl"></div>
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="px-4">
          <Form>
            <Form.Group className="mb-2">
              <FloatingLabel controlId="cat-name" label="Category name" className="required-field">
                <Form.Control
                  type="text"
                  value={categoryForm.name}
                  onChange={(e) => setCategoryForm((p) => ({ ...p, name: e.target.value }))}
                  placeholder="e.g., ICU pearls"
                />
              </FloatingLabel>
            </Form.Group>
            <Form.Group className="mb-2">
              <FloatingLabel controlId="cat-desc" label="Description (optional)">
                <Form.Control
                  as="textarea"
                  style={{ minHeight: 90 }}
                  value={categoryForm.description}
                  onChange={(e) => setCategoryForm((p) => ({ ...p, description: e.target.value }))}
                  placeholder="What belongs in this category?"
                />
              </FloatingLabel>
            </Form.Group>
            <Form.Group className="mb-2 d-flex flex-row justify-content-start align-items-center gap-2">
              <Form.Label className="small text-muted fw-semibold mb-0">Theme color</Form.Label>
              <div className="d-flex align-items-center gap-3 bg-light rounded-3 ">
                <Form.Control
                  type="color"
                  value={categoryForm.themeColor}
                  onChange={(e) => setCategoryForm((p) => ({ ...p, themeColor: e.target.value }))}
                  style={{ width: 46, height: 36, padding: 2 }}
                />
                <div className="small fw-semibold text-muted pe-3">{categoryForm.themeColor}</div>
              </div>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer className="border-top-0 px-4">
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => {
              setShowCreateCategory(false);
              resetCategoryForm();
            }}
          >
            Cancel
          </Button>
          <Button variant="primary" size="sm" onClick={createCategory} disabled={creatingCategory}>
            {creatingCategory ? "Creating…" : "Create category"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Create Post */}
      <Modal
        show={showCreatePost}
        onHide={() => {
          setShowCreatePost(false);
          resetPostForm();
        }}
        size="lg"
      >
        <Modal.Header closeButton className="border-bottom-0 px-4">
          <Modal.Title className="w-100">
            <div className="d-flex flex-column justify-content-start align-items-start">
              <span>New Discussion</span>
              <div className="text-hl"></div>
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="px-4">
          <Form>
            <Row className="g-2">
              <Col md={8}>
                <Form.Group className="mb-3">
                  <FloatingLabel controlId="post-title" label="Title" className="required-field">
                    <Form.Control
                      type="text"
                      value={postForm.title}
                      onChange={(e) => setPostForm((p) => ({ ...p, title: e.target.value }))}
                      placeholder="Enter a title"
                    />
                  </FloatingLabel>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <FloatingLabel controlId="post-category" label="Category">
                    <Form.Select
                      value={postForm.categoryId}
                      onChange={(e) => setPostForm((p) => ({ ...p, categoryId: e.target.value }))}
                    >
                      <option value="">None</option>
                      {categories.map((c) => (
                        <option key={c.id} value={String(c.id)}>
                          {c.name}
                        </option>
                      ))}
                    </Form.Select>
                  </FloatingLabel>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <FloatingLabel controlId="post-hashtags" label="Hashtags (comma or space separated)">
                <Form.Control
                  type="text"
                  value={postForm.hashtags}
                  onChange={(e) => setPostForm((p) => ({ ...p, hashtags: e.target.value }))}
                  placeholder="#icu #airway #pearls"
                />
              </FloatingLabel>
            </Form.Group>

            <Form.Group className="mb-3">
              <FloatingLabel controlId="post-audience" label="Who can see this post?">
                <Form.Select
                  value={postForm.audience}
                  onChange={(e) => setPostForm((p) => ({ ...p, audience: e.target.value, allowedRoles: [], allowedUserIds: [] }))}
                >
                  <option value="all">All users</option>
                  <option value="roles">Selected roles</option>
                  <option value="users">Selected users (coming soon)</option>
                </Form.Select>
              </FloatingLabel>
              {postForm.audience === "roles" && (
                <div className="mt-2 d-flex flex-wrap gap-2">
                  {["director", "resident", "attending", "admin", "student", "administrative assistant"].map((r) => (
                    <Button
                      key={r}
                      size="sm"
                      variant={postForm.allowedRoles.includes(r) ? "light-solid" : "outline-secondary"}
                      onClick={() =>
                        setPostForm((p) => ({
                          ...p,
                          allowedRoles: p.allowedRoles.includes(r) ? p.allowedRoles.filter((x) => x !== r) : [...p.allowedRoles, r]
                        }))
                      }
                    >
                      {r}
                    </Button>
                  ))}
                </div>
              )}
              {postForm.audience === "users" && (
                <div className="small text-muted mt-2">
                  User-specific visibility UI will be added next (the backend supports it already).
                </div>
              )}
            </Form.Group>

            <Form.Group className="mb-2">
              <Form.Label className="small text-muted required-field">Content</Form.Label>
              <RichTextEditor
                value={postForm.contentHtml}
                onChange={(html) => setPostForm((p) => ({ ...p, contentHtml: html }))}
                placeholder="Write your discussion topic here…"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer className="border-top-0 px-4">
          <Button
            variant="outline-secondary"
            onClick={() => {
              setShowCreatePost(false);
              resetPostForm();
            }}
          >
            Cancel
          </Button>
          <Button variant="primary" onClick={createPost} disabled={creatingPost}>
            {creatingPost ? "Posting…" : "Post discussion"}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default DiscussionsPage;

